import React, { useRef, useEffect } from 'react'
import styled from 'react-emotion'
import Icon from 'common/components/Icon'
import Text from 'common/components/Text'
import BlockImageContainer from './BlockImageContainer'
import CropHandle from './CropHandle'
import ResizeHandle from './ResizeHandle'
import { placeholders } from 'variables'
import useClickOutside from '../../../hooks/useClickOutside'

const BlockLoader = styled(({ className, progress }) => (
  <span className={className}>
    {progress ? (
      <Text medium grey>
        {Math.round(progress * 100)}%
      </Text>
    ) : (
      <Icon icon="loader" />
    )}
  </span>
))({
  background: 'white',
  position: 'absolute',
  top: 5,
  right: 5,
  minWidth: 20,
  height: 20,
  borderRadius: 2,
  padding: '0 5px',
  display: 'flex',
  alignItems: 'center',
  fontFeatureSettings: '"tnum"'
})

const cropStlye = crop => ({
  position: 'absolute',
  width: crop ? crop.scale * 100 + '%' : '100%',
  top: crop ? -crop.top * 100 + '%' : 0,
  left: crop ? -crop.left * 100 + '%' : 0
})

// Shows full image and resize handles while cropping
const CropView = styled(({ src, className }) => (
  <div className={className}>
    <BlockImageImg
      src={src.src}
      fixOrientation={src.fixOrientation}
      style={translucent}
    />
    <ResizeHandle direction="ne" />
    <ResizeHandle direction="nw" />
    <ResizeHandle direction="se" />
    <ResizeHandle direction="sw" />
  </div>
))(({ crop }) => ({
  label: 'CropView',
  ...cropStlye(crop),
  [BlockImageImg]: { position: 'static', display: 'block' }
}))
const translucent = { opacity: 0.7 }

// contains cropped image and hides overflow
const Cropper = styled('div')({
  label: 'Cropper',
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  overflow: 'hidden'
})

const BlockImageImg = styled('img')(
  ({
    crop,
    loaded = true,
    fixOrientation = {},
    isPlaceholder = false
    // fadeIn = false
  }) => ({
    label: 'BlockImage',
    ...cropStlye(crop),
    userSelect: 'none',
    pointerEvents: 'none',
    filter: isPlaceholder && `blur(${placeholders.blur}px)`,
    transform: isPlaceholder ? 'scale(1.1)' : 'translateZ(0)', // TODO fixOrientation will override
    // transition: fadeIn && `opacity ${placeholders.fadeTime}s`,
    opacity: !loaded && '0',
    ...fixOrientation
  })
)

const directions = 'n,s,e,w,ne,se,nw,sw'.split(',')
const BlockImage = styled(
  ({
    observe,
    cropDone,
    name,
    crop,
    src,
    previousSrc,
    uploading,
    uploadProgress,
    location,
    loaded,
    onLoad,
    toggleCrop,
    cropping,
    className
  }) => {
    const ref = useRef(null)
    // done on click outside
    useClickOutside(ref, cropping && cropDone)
    useEffect(() => {
      observe(ref.current)
    }, [ref.current])
    return (
      <div className={className} onDoubleClick={toggleCrop} ref={ref}>
        {/* Full image outside crop area & handles */}
        {cropping && <CropView crop={crop} src={previousSrc || src} />}

        <Cropper cropDone={cropDone}>
          {/* keep prev src around until new one is ready */}
          {previousSrc && (
            <BlockImageImg
              crop={crop}
              src={previousSrc.src}
              isPlaceholder={previousSrc.isPlaceholder}
              fixOrientation={previousSrc.fixOrientation}
              alt={name}
            />
          )}
          {src && (
            <CancelableBlockImageImg
              crop={crop}
              src={src.src}
              isPlaceholder={src.isPlaceholder}
              fadeIn={
                location !== 'single' &&
                (!previousSrc ||
                  (previousSrc.isPlaceholder && !src.isPlaceholder))
              }
              alt={name}
              loaded={loaded}
              fixOrientation={src.fixOrientation}
              onLoad={() => onLoad()}
            />
          )}

          {cropping &&
            directions.map(d => <CropHandle direction={d} key={d} />)}
        </Cropper>

        {(!loaded || uploading) && <BlockLoader progress={uploadProgress} />}
      </div>
    )
  }
)(({ loaded, cropping }) => ({
  label: 'BlockImage',
  height: '100%',
  background: !loaded && 'rgba(242, 242, 242, 0.5)',
  overflow: 'hidden',
  cursor: cropping && 'move'
}))

export default BlockImageContainer(BlockImage)

class CancelableBlockImageImg extends React.Component {
  componentDidMount() {
    this.mounted = true
  }

  componentWillUnmount() {
    this.mounted = false
    this.timeout && clearTimeout(this.timeout)
  }
  render() {
    return (
      <BlockImageImg
        {...this.props}
        onLoad={() => {
          if (this.mounted) this.timeout = this.props.onLoad()
        }}
      />
    )
  }
}
